import React, { useEffect, useMemo } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { PageBreadcrumbs } from "../../../components/common/PageBreadcrumbs";
import { NavigationBlocker } from "../../../components/common/NavigationBlocker";
import { useShopifySaveBar } from "../../../hooks/useShopifySaveBar";
import {
  useBundleEditorStore,
  selectBundleIsDirty,
} from "../../../store/useBundleEditorStore";
import { useOffersListStore } from "../../../store/useOffersListStore";
import { getBundleValidationErrors } from "../../../store/bundle-editor.mapping";
import { SalesBoosterTabBar } from "../SalesBoosterTabBar";
import {
  NameSection,
  TriggerProductSection,
  BundleTypeSection,
} from "./BundleEditorConfig";
import { FixedItemsSection } from "./BundleEditorFixedItems";
import { ConfigurableGroupsSection } from "./BundleEditorConfigurableGroups";
import { PricingSection } from "./BundleEditorPricing";
import { DesignSection } from "./BundleEditorDesign";
import { BundleEditorPreview } from "./BundleEditorPreview";

// ─── Page ─────────────────────────────────────────────────────────────────────

const BundleEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();

  const {
    config,
    offerId,
    isLoading,
    isSaving,
    updateField,
    setBundleType,
    addItems,
    removeItem,
    updateItem,
    addGroup,
    removeGroup,
    updateGroup,
    addOptions,
    removeOption,
    initNew,
    loadFromBackend,
    save,
    discardChanges,
    reset,
  } = useBundleEditorStore(
    useShallow((s) => ({
      config: s.config,
      offerId: s.offerId,
      isLoading: s.isLoading,
      isSaving: s.isSaving,
      updateField: s.updateField,
      setBundleType: s.setBundleType,
      addItems: s.addItems,
      removeItem: s.removeItem,
      updateItem: s.updateItem,
      addGroup: s.addGroup,
      removeGroup: s.removeGroup,
      updateGroup: s.updateGroup,
      addOptions: s.addOptions,
      removeOption: s.removeOption,
      initNew: s.initNew,
      loadFromBackend: s.loadFromBackend,
      save: s.save,
      discardChanges: s.discardChanges,
      reset: s.reset,
    }))
  );

  const isDirty = useBundleEditorStore(selectBundleIsDirty);
  const refetchOffers = useOffersListStore((s) => s.refetch);

  // Required-field validation, shared with the save-time guard in the
  // mapping so the gate and the server-call check can't diverge.
  const validationErrors = useMemo(
    () => getBundleValidationErrors(config),
    [config]
  );
  const canSave = validationErrors.length === 0;

  useEffect(() => {
    if (routeId) {
      // Skip when we already have the offer in the store — this guards
      // the post-create redirect (`navigate(..., { replace: true })`),
      // which would otherwise trigger a redundant fetch + loading
      // flicker even though the just-saved state is already loaded.
      if (offerId === routeId) return;
      loadFromBackend(routeId).catch(() => {
        window.shopify?.toast?.show("No se pudo cargar la oferta.", {
          isError: true,
        });
      });
    } else if (offerId === null) {
      initNew();
    }
  }, [routeId, offerId, initNew, loadFromBackend]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Save bar is wired against the store. Save itself is a TODO until
  // Chunk 4 — the store throws and we surface it as a toast.
  useShopifySaveBar({
    isDirty,
    isSaving,
    canSave,
    onSave: async () => {
      // Safety net for any path that triggers save while the button is
      // disabled. Throwing keeps the save bar open and surfaces the reason —
      // we must NOT swallow errors here, otherwise the hook reports a false
      // "Guardado correctamente" while isDirty stays true and the merchant
      // gets trapped by the navigation blocker.
      if (validationErrors.length > 0) {
        throw new Error(validationErrors[0]);
      }
      const savedId = await save();
      // Bust the offers-list cache so the new/updated bundle is reflected
      // when the merchant navigates back (mirrors the upsell editors).
      refetchOffers().catch(() => {});
      if (!routeId) {
        navigate(`/sales-booster/bundles/${savedId}`, {
          replace: true,
        });
      }
    },
    onDiscard: discardChanges,
  });

  const hasContent =
    config.items.length > 0 ||
    config.groups.some((g) => g.options.length > 0) ||
    config.groups.length > 0;

  return (
    <div>
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading={routeId ? "Editar bundle" : "Nuevo bundle"}>
        <PageBreadcrumbs
          items={[
            { label: "Impulsor de ventas", to: "/sales-booster" },
            { label: "Bundles", to: "/sales-booster/bundles" },
          ]}
        />
        <s-stack direction="block" gap="large-200">
          <SalesBoosterTabBar />

          {/* ── Header (active toggle + status) ── */}
          <div className="flex items-center justify-end py-1">
            <div className="flex items-center gap-3">
              <s-switch
                label="Activar bundle"
                labelAccessibilityVisibility="exclusive"
                checked={config.isActive}
                onChange={(e: CallbackEvent<"s-switch">) =>
                  updateField("isActive", e.currentTarget.checked)
                }
              />
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide border ${
                  config.isActive
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-gray-100 text-gray-500 border-gray-200"
                }`}
              >
                {config.isActive ? "Activo" : "Inactivo"}
              </span>
            </div>
          </div>

          {isLoading && (
            <s-banner tone="info">Cargando configuración del bundle…</s-banner>
          )}

          {isDirty && validationErrors.length > 0 && (
            <s-banner tone="warning">
              <s-stack direction="block" gap="small-200">
                <s-text type="strong">
                  Completa lo siguiente para poder guardar:
                </s-text>
                {validationErrors.map((err, i) => (
                  <s-text key={i}>• {err}</s-text>
                ))}
              </s-stack>
            </s-banner>
          )}

          {/* ── Two-column layout ── */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            {/* Left: sections */}
            <s-stack direction="block" gap="base">
              <NameSection
                name={config.internalName}
                onNameChange={(v) => updateField("internalName", v)}
              />

              <TriggerProductSection
                products={config.triggerProducts}
                onProductsChange={(products) =>
                  updateField("triggerProducts", products)
                }
              />

              <BundleTypeSection
                bundleType={config.bundleType}
                hasContent={hasContent}
                onChange={setBundleType}
              />

              {config.bundleType === "fixed" ? (
                <FixedItemsSection
                  items={config.items}
                  onAdd={(picks) =>
                    addItems(
                      picks.map((pick) => ({
                        productId: pick.productId,
                        variantId: pick.variantId,
                        productTitle: pick.productTitle,
                        ...(pick.variantTitle
                          ? { variantTitle: pick.variantTitle }
                          : {}),
                        ...(pick.imageUrl ? { imageUrl: pick.imageUrl } : {}),
                        basePrice: pick.basePrice,
                        quantity: 1,
                      }))
                    )
                  }
                  onRemove={removeItem}
                  onUpdate={updateItem}
                />
              ) : (
                <ConfigurableGroupsSection
                  groups={config.groups}
                  onAddGroup={addGroup}
                  onRemoveGroup={removeGroup}
                  onUpdateGroup={updateGroup}
                  onAddOption={(gi, picks) =>
                    addOptions(
                      gi,
                      picks.map((pick) => ({
                        productId: pick.productId,
                        variantId: pick.variantId,
                        productTitle: pick.productTitle,
                        ...(pick.variantTitle
                          ? { variantTitle: pick.variantTitle }
                          : {}),
                        ...(pick.imageUrl ? { imageUrl: pick.imageUrl } : {}),
                        basePrice: pick.basePrice,
                      }))
                    )
                  }
                  onRemoveOption={removeOption}
                />
              )}

              <PricingSection
                bundleType={config.bundleType}
                items={config.items}
                basePrice={config.basePrice}
                compareAtPrice={config.compareAtPrice}
                hideCompareAtPrice={config.hideCompareAtPrice}
                pricingAction={config.pricingAction}
                pricingAmount={config.pricingAmount}
                pricingPercentage={config.pricingPercentage}
                onBasePriceChange={(v) => updateField("basePrice", v)}
                onCompareAtPriceChange={(v) => updateField("compareAtPrice", v)}
                onHideCompareAtPriceChange={(v) =>
                  updateField("hideCompareAtPrice", v)
                }
                onPricingActionChange={(v) => updateField("pricingAction", v)}
                onPricingAmountChange={(v) => updateField("pricingAmount", v)}
                onPricingPercentageChange={(v) =>
                  updateField("pricingPercentage", v)
                }
              />

              <DesignSection
                titleOverride={config.titleOverride}
                description={config.description}
                imageUrl={config.imageUrl}
                backgroundColor={config.backgroundColor}
                borderColor={config.borderColor}
                activeBorderColor={config.activeBorderColor}
                borderRadius={config.borderRadius}
                onTitleOverrideChange={(v) => updateField("titleOverride", v)}
                onDescriptionChange={(v) => updateField("description", v)}
                onImageUrlChange={(v) => updateField("imageUrl", v)}
                onBackgroundColorChange={(v) =>
                  updateField("backgroundColor", v)
                }
                onBorderColorChange={(v) => updateField("borderColor", v)}
                onActiveBorderColorChange={(v) =>
                  updateField("activeBorderColor", v)
                }
                onBorderRadiusChange={(v) => updateField("borderRadius", v)}
              />
            </s-stack>

            {/* Right: preview */}
            <BundleEditorPreview state={config} />
          </div>
        </s-stack>
      </s-page>
    </div>
  );
};

export default BundleEditor;
